import React, { useState } from "react";
import { cryptiqueAnalyticsTheme as theme } from "../theme";
import { formatNumber } from "../../../../shared/utils/numberFormatter";
import { makeFormatValue } from "../utils/axisFormat";

/**
 * Funnel breakdown bar chart
 *
 * - One horizontal bar per breakdown segment
 * - Bars are sized by the *last step* value for that segment
 * - Supports multiple measuring types:
 *   - conversion_rate
 *   - unique_users
 *   - total_conversions
 *   - total_sessions
 *   - property_sum
 *   - time_to_convert
 *
 * Props:
 * - steps: overall funnel steps array (used to know the last step index, first/last labels, etc.)
 * - breakdowns: array of breakdown objects from the API
 * - measuring: optional explicit measuring type; falls back to last step's `measuring` or "conversion_rate"
 *
 * Each breakdown is expected to look like:
 * {
 *   breakdownValue | breakdownKey,
 *   breakdownValues?, // array for hierarchical breakdowns (e.g. ["Android", "Abuja"])
 *   overallConversion?, // number (% from backend)
 *   steps: {
 *     step1: { count, value?, conversionRate?, measuring?, timeToConvert? },
 *     step2: { ... },
 *     ...
 *   }
 * }
 */
export default function FunnelBreakdownBarChart({
  steps = [],
  breakdowns = [],
  measuring: measuringProp,
  config,
}) {
  // Tooltip state: which segment is hovered (must be declared before any early returns)
  const [hoveredId, setHoveredId] = useState(null);

  const hasValidData =
    Array.isArray(steps) &&
    steps.length > 0 &&
    Array.isArray(breakdowns) &&
    breakdowns.length > 0;

  if (!hasValidData) {
    return (
      <div className="flex items-center justify-center h-[200px]">
        <div className="text-sm" style={{ color: theme.textMuted }}>
          No breakdown data available
        </div>
      </div>
    );
  }

  const firstStep = steps[0];
  const lastStep = steps[steps.length - 1];
  const lastStepKey = `step${steps.length}`;

  // Axis formatting: respect funnelAxis + left/right Y config
  const leftY = config?.axis?.leftY ?? {};
  const rightY = config?.axis?.rightY ?? {};
  const funnelAxis = config?.funnelAxis ?? "left";
  const yAxisConfig = funnelAxis === "right" ? rightY : leftY;
  const formatYValue = makeFormatValue(yAxisConfig);

  // Final measuring type
  const measuring =
    measuringProp ||
    lastStep?.measuring ||
    firstStep?.measuring ||
    "conversion_rate";

  // Helper: format time interval to compact string (used for time_to_convert)
  const formatTimeInterval = (interval) => {
    if (!interval) return "—";
    if (typeof interval === "string") return interval;
    if (typeof interval === "object" && interval !== null) {
      const { days = 0, hours = 0, minutes = 0, seconds = 0 } = interval;
      if (days > 0) {
        const dayFraction = days + hours / 24;
        return `${dayFraction.toFixed(1)}D`;
      }
      if (hours > 0) return `${hours}h`;
      if (minutes > 0) return `${minutes}m`;
      return `${seconds}s`;
    }
    return String(interval);
  };

  // Build funnel title used in tooltip header
  const firstLabel =
    firstStep?.label || firstStep?.eventName || "step 1";
  const lastLabel =
    lastStep?.label || lastStep?.eventName || `step ${steps.length}`;

  const measuringTitlePrefix = (() => {
    switch (measuring) {
      case "unique_users":
        return "Uniques of";
      case "total_conversions":
        return "Total Conversions of";
      case "total_sessions":
        return "Total Sessions of";
      case "property_sum":
        return "Property Sum of";
      case "time_to_convert":
        return "Time to Convert for";
      case "conversion_rate":
      default:
        return "Conversion Rate of";
    }
  })();

  const tooltipTitle = `${measuringTitlePrefix} ${firstLabel} through ${lastLabel} – All Steps`;

  // Total users in last step (overall) – used for "% of overall" in tooltip
  const overallLastStepCount = Number(lastStep?.count || 0) || 0;

  // Flatten breakdowns into simple segments we can render as bars.
  const segments = breakdowns.map((bd, index) => {
    const baseLabel =
      (Array.isArray(bd.breakdownValues) && bd.breakdownValues.length > 0
        ? bd.breakdownValues.join(" / ")
        : null) ||
      bd.breakdownValue ||
      bd.breakdownKey ||
      "Unknown";

    const lastStepData = bd.steps?.[lastStepKey] || {};
    const count = Number(lastStepData.count || 0) || 0;

    // Backend may send a specific measuring for the step
    const stepMeasuring = lastStepData.measuring || measuring;

    const rawValue =
      typeof lastStepData.value === "number"
        ? lastStepData.value
        : undefined;

    // Primary numeric value for this segment, depending on measuring type
    let primaryValue; // number | string (for time)
    let primaryLabel; // e.g. "73 users", "73 total conversions", "1.8D", etc.

    switch (stepMeasuring) {
      case "unique_users": {
        const v = rawValue != null ? rawValue : count;
        primaryValue = v;
        primaryLabel = `${formatYValue(v)} users`;
        break;
      }
      case "total_conversions": {
        const v = rawValue != null ? rawValue : 0;
        primaryValue = v;
        primaryLabel = `${formatYValue(v)} total conversions`;
        break;
      }
      case "total_sessions": {
        const v = rawValue != null ? rawValue : 0;
        primaryValue = v;
        primaryLabel = `${formatYValue(v)} total sessions`;
        break;
      }
      case "property_sum": {
        const v = rawValue != null ? rawValue : 0;
        primaryValue = v;
        primaryLabel = formatYValue(v);
        break;
      }
      case "time_to_convert": {
        const timeVal = rawValue || lastStepData.timeToConvert;
        primaryValue = timeVal;
        primaryLabel = formatTimeInterval(timeVal);
        break;
      }
      case "conversion_rate":
      default: {
        const v = count;
        primaryValue = v;
        primaryLabel = `${formatYValue(v)} users`;
        break;
      }
    }

    // Overall conversion for this segment
    const segmentOverallConv =
      typeof bd.overallConversion === "number"
        ? bd.overallConversion
        : (() => {
            // fallback: from counts first/last if available
            const firstKey = "step1";
            const firstForBd = bd.steps?.[firstKey];
            const firstCount = Number(firstForBd?.count || 0) || 0;
            if (!firstCount) return 0;
            return (count / firstCount) * 100;
          })();

    // Segment share of overall (based on users in last step)
    const shareOfOverall =
      overallLastStepCount > 0
        ? (count / overallLastStepCount) * 100
        : 0;

    // Value used for bar length – we always use primary numeric value when numeric,
    // otherwise fall back to count so time_to_convert still has a bar.
    const numericForBar =
      typeof primaryValue === "number" ? primaryValue : count;

    return {
      id: index,
      label: baseLabel,
      users: count,
      primaryValue,
      primaryLabel,
      conversion: segmentOverallConv, // %
      shareOfOverall, // %
      numericForBar,
    };
  });

  const maxValue =
    segments.reduce(
      (max, s) => (s.numericForBar > max ? s.numericForBar : max),
      0
    ) || 1;

  // Build tooltip lines per segment, matching Mixpanel style
  const getTooltipLines = (segment) => {
    const conv = Number(segment.conversion || 0);
    const share = Number(segment.shareOfOverall || 0);

    if (measuring === "conversion_rate") {
      // 73.031% converted
      // 306 users
      // 100% of overall
      return [
        `${formatYValue(conv)} converted`,
        `${formatNumber(segment.users)} users`,
        `${share.toFixed(2)}% of overall`,
      ];
    }

    // Other measuring types: primary first, then % converted, then % of overall
    const lines = [
      segment.primaryLabel,
      `${conv.toFixed(4)}% converted`,
      `${share.toFixed(2)}% of overall`,
    ];
    return lines;
  };

  return (
    <div
      className="w-full space-y-3"
      style={{
        fontFamily: "Raleway, sans-serif",
        backgroundColor: "#ffffff",
      }}
    >
      {segments.map((segment) => {
        const widthPercent = Math.max(
          2,
          Math.min(100, (segment.numericForBar / maxValue) * 100)
        );
        const barColor = theme.primaryLight || "#4B57AA";
        const isHovered = hoveredId === segment.id;
        const tooltipLines = getTooltipLines(segment);

        // Side label: for conversion_rate show \"% • users\", for other types show the primary label (e.g. \"655 total conversions\")
        const sideLabel =
          measuring === "conversion_rate"
            ? `${segment.conversion.toFixed(2)}% • ${formatNumber(segment.users)} users`
            : segment.primaryLabel;

        return (
          <div
            key={segment.id}
            className="flex items-center gap-3 relative group"
            onMouseEnter={() => setHoveredId(segment.id)}
            onMouseLeave={() => setHoveredId(null)}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <span
                  className="text-sm font-medium truncate"
                  style={{ color: theme.textPrimary }}
                  title={segment.label}
                >
                  {segment.label}
                </span>
                <span className="text-xs" style={{ color: theme.textMuted }}>
                  {sideLabel}
                </span>
              </div>
              <div
                className="h-3 rounded-full overflow-hidden"
                style={{
                  backgroundColor: theme.bgOverlay || "#f3f4f6",
                }}
              >
                <div
                  className="h-full rounded-full transition-all duration-200"
                  style={{
                    width: `${widthPercent}%`,
                    backgroundColor: barColor,
                    boxShadow: isHovered
                      ? "0 4px 8px rgba(0,0,0,0.25)"
                      : "0 1px 2px rgba(0,0,0,0.15)",
                  }}
                />
              </div>
            </div>

            {/* Tooltip – positioned to the right of the bar, similar to Mixpanel */}
            {isHovered && (
              <div
                className="absolute z-40 bg-white border shadow-lg rounded-lg px-3 py-2 text-xs"
                style={{
                  right: 0,
                  top: "100%",
                  marginTop: 8,
                  minWidth: 240,
                  maxWidth: 320,
                  borderColor: theme.border,
                }}
              >
                <div
                  className="mb-1 font-semibold"
                  style={{ color: theme.textPrimary }}
                >
                  {tooltipTitle}
                </div>
                <div
                  className="mb-1"
                  style={{ color: theme.textSecondary }}
                >
                  {segment.label}
                </div>
                {tooltipLines.map((line, idx) => (
                  <div
                    key={idx}
                    className="leading-snug"
                    style={{
                      color:
                        idx === 0
                          ? theme.textPrimary
                          : idx === 2
                          ? theme.textMuted
                          : theme.textPrimary,
                      marginTop: idx === 0 ? 2 : 0,
                    }}
                  >
                    {line}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
